/**
 * Flabom — calculadora de frete do cart drawer ("Calcule seu frete", Figma 130:2 / 133:2).
 *
 * Fluxo oficial da AJAX Cart API do Shopify:
 *   1. POST  {cart_url}/prepare_shipping_rates.json?shipping_address[zip]=00000-000
 *            &shipping_address[country]=BR&shipping_address[province]=UF
 *      -> 200 com corpo vazio quando o endereço é aceito; 422 + JSON de erros quando não.
 *      A loja EXIGE `province`: sem ele o Shopify responde 422 e nenhuma opção aparece.
 *      Como o cliente só digita o CEP, a UF é derivada das faixas oficiais dos Correios
 *      (ver `ZIP_RANGES`) — nenhuma chamada externa, nenhum campo novo na interface.
 *   2. GET   {cart_url}/async_shipping_rates.json
 *      -> devolve `null` enquanto o cálculo roda; depois `{"shipping_rates":[{name, price}, ...]}`.
 *
 * A opção escolhida atualiza a linha "Entrega" do resumo (`[data-fb-shipping-value]`) e o
 * "Total estimado" = `cart.total_price` (produtos, já com descontos; `data-fb-total-cents`)
 * + tarifa escolhida. Sem tarifa, o resumo fica no estado "Total sem frete" do Liquid.
 * A cada `cart:update` (quantidade, remoção, item novo) o drawer recota para o carrinho
 * novo, mantendo a transportadora escolhida quando ela continua disponível.
 *
 * Modo PDP (`data-quote-variant`): a API só cota o CARRINHO, então com carrinho vazio
 * a cotação falhava. Aqui a variante selecionada (+ quantidade) entra no carrinho só
 * durante a cotação e sai logo depois — ver `#withQuotedVariant`.
 */

import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'flabom:cart-shipping';
const POLL_INTERVAL = 500;
const POLL_ATTEMPTS = 24; // ~12s

/**
 * @param {number} cents
 * @param {number} precision
 * @param {string} thousands
 * @param {string} decimal
 * @returns {string}
 */
function formatWithDelimiters(cents, precision, thousands, decimal) {
  const amount = (Number(cents) / 100).toFixed(precision);
  const [integer, fraction] = amount.split('.');
  const grouped = integer.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
  return fraction ? `${grouped}${decimal}${fraction}` : grouped;
}

/**
 * Reimplementa o filtro `money` do Liquid a partir de `shop.money_format`,
 * para o valor formatado no JS bater com o que o Liquid renderiza.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const template = format || '${{amount}}';
  const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
  const key = match ? match[1] : 'amount';

  let value;
  switch (key) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0, ',', '.');
      break;
    // O Shopify usa "separator" (ex.: R$ {{amount_with_comma_separator}}); os
    // apelidos em "separated" aparecem em temas antigos e ficam aceitos aqui.
    case 'amount_with_comma_separator':
    case 'amount_with_comma_separated':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
    case 'amount_no_decimals_with_comma_separated':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
    case 'amount_with_space_separated':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
    case 'amount_no_decimals_with_space_separated':
      value = formatWithDelimiters(cents, 0, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
    case 'amount_with_apostrophe_separated':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2, ',', '.');
      break;
  }

  return template.replace(/\{\{\s*\w+\s*\}\}/, value);
}

/**
 * @param {string} value
 * @returns {string} só os dígitos, no máximo 8
 */
function onlyDigits(value) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, 8);
}

/**
 * Máscara 00000-000.
 * @param {string} value
 * @returns {string}
 */
function maskZip(value) {
  const digits = onlyDigits(value);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/**
 * Faixas oficiais de CEP dos Correios -> UF. Cada entrada é [inicio, fim, uf] com os
 * 5 primeiros dígitos do CEP (prefixo). Usada para preencher
 * `shipping_address[province]`, que a API de frete da loja exige.
 * @type {Array<[number, number, string]>}
 */
const ZIP_RANGES = [
  [1000, 19999, 'SP'],
  [20000, 28999, 'RJ'],
  [29000, 29999, 'ES'],
  [30000, 39999, 'MG'],
  [40000, 48999, 'BA'],
  [49000, 49999, 'SE'],
  [50000, 56999, 'PE'],
  [57000, 57999, 'AL'],
  [58000, 58999, 'PB'],
  [59000, 59999, 'RN'],
  [60000, 63999, 'CE'],
  [64000, 64999, 'PI'],
  [65000, 65999, 'MA'],
  [66000, 68899, 'PA'],
  [68900, 68999, 'AP'],
  [69000, 69299, 'AM'],
  [69300, 69399, 'RR'],
  [69400, 69899, 'AM'],
  [69900, 69999, 'AC'],
  [70000, 73699, 'DF'],
  [73700, 76799, 'GO'],
  [76800, 76999, 'RO'],
  [77000, 77999, 'TO'],
  [78000, 78899, 'MT'],
  [79000, 79999, 'MS'],
  [80000, 87999, 'PR'],
  [88000, 89999, 'SC'],
  [90000, 99999, 'RS'],
];

/**
 * Deriva a UF a partir dos 8 dígitos do CEP.
 * @param {string} digits
 * @returns {string} sigla da UF, ou '' quando o CEP está fora das faixas conhecidas
 */
function provinceFromZip(digits) {
  const prefix = parseInt(String(digits || '').slice(0, 5), 10);
  if (!Number.isFinite(prefix)) return '';
  const range = ZIP_RANGES.find(([start, end]) => prefix >= start && prefix <= end);
  return range ? range[2] : '';
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class FlabomShippingCalculator extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #busy = false;
  /** CEP (8 dígitos) das tarifas na tela; '' quando não há tarifa escolhida. */
  #ratedZip = '';

  connectedCallback() {
    this.toggleButton = this.querySelector('.fb-ship__toggle');
    this.panel = this.querySelector('.fb-ship__panel');
    this.input = this.querySelector('.fb-ship__input');
    this.submitButton = this.querySelector('.fb-ship__submit');
    this.messageEl = this.querySelector('.fb-ship__message');
    this.ratesEl = this.querySelector('.fb-ship__rates');

    if (!this.toggleButton || !this.panel || !this.input || !this.submitButton) return;

    this.toggleButton.addEventListener('click', this.#onToggle);
    this.submitButton.addEventListener('click', this.#onSubmit);
    this.input.addEventListener('input', this.#onInput);
    this.input.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#restore();
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.#onToggle);
    this.submitButton?.removeEventListener('click', this.#onSubmit);
    this.input?.removeEventListener('input', this.#onInput);
    this.input?.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#abort?.abort();
  }

  /* ------------------------------- estado ------------------------------- */

  /** @returns {{zip?: string, open?: boolean}} */
  #readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  /** @param {{zip?: string, open?: boolean}} patch */
  #writeState(patch) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.#readState(), ...patch }));
    } catch {
      /* sessão sem storage: o bloco segue funcionando, só não lembra o CEP */
    }
  }

  /**
   * O drawer é remontado (morphSection) a cada mudança de quantidade: recupera o
   * CEP e recalcula, para o frete nunca ficar defasado em relação ao carrinho.
   */
  #restore() {
    const { zip, open } = this.#readState();
    if (!zip) return;

    this.input.value = maskZip(zip);
    // Na PDP cotar mexe no carrinho: só cota quando o cliente pede.
    if (!open || this.dataset.quoteVariant != null) return;

    this.#setExpanded(true);
    if (onlyDigits(zip).length === 8) this.#calculate();
  }

  /* -------------------------------- UI ---------------------------------- */

  #onToggle = () => {
    this.#setExpanded(this.toggleButton.getAttribute('aria-expanded') !== 'true');
  };

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    this.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.panel.hidden = !expanded;
    this.#writeState({ open: expanded });
    if (expanded) this.input.focus({ preventScroll: true });
  }

  #onInput = () => {
    const masked = maskZip(this.input.value);
    if (this.input.value !== masked) this.input.value = masked;
    this.#writeState({ zip: onlyDigits(masked) });

    // CEP mudou: as tarifas na tela são de outro endereço; tira elas e o frete do total.
    if (this.#ratedZip && onlyDigits(masked) !== this.#ratedZip) {
      this.#abort?.abort();
      this.#setMessage('', false);
      this.#renderRates([]);
      this.#setShippingValue(null);
    }
  };

  /**
   * Carrinho mudou: o morph do drawer devolve o resumo ao estado "sem frete" com o
   * subtotal novo; recota para o carrinho atual. Só no drawer: na PDP a cotação mexe
   * no carrinho e não pode disparar sozinha.
   */
  #onCartUpdate = () => {
    if (!this.#ratedZip || this.dataset.quoteVariant != null) return;
    // Espera o morphSection (síncrono, logo após o evento) aplicar o HTML novo.
    requestAnimationFrame(() => {
      this.#abort?.abort();
      this.#busy = false;
      this.#calculate();
    });
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#calculate();
  };

  #onSubmit = () => this.#calculate();

  /**
   * @param {string} text
   * @param {boolean} [isError]
   */
  #setMessage(text, isError) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.hidden = !text;
    this.messageEl.classList.toggle('fb-ship__message--error', Boolean(isError));
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.submitButton.disabled = busy;
  }

  /** Linha "Entrega" do resumo. */
  #shippingValueEl() {
    return this.closest('.fb-cart-summary')?.querySelector('[data-fb-shipping-value]') ?? null;
  }

  /**
   * Linha "Entrega" + total do resumo. `null` = sem tarifa (estado "Total sem frete").
   * @param {{name: string, cents: number} | null} rate
   */
  #setShippingValue(rate) {
    if (!rate) this.#ratedZip = '';

    const el = this.#shippingValueEl();
    if (el) el.textContent = rate ? this.#money(rate.cents) : el.dataset.fbShippingPending ?? '';

    const summary = this.closest('.fb-cart-summary');
    if (!(summary instanceof HTMLElement)) return;

    const valueEl = summary.querySelector('[data-fb-total-value]');
    if (valueEl instanceof HTMLElement) {
      const cents = (Number(summary.dataset.fbTotalCents) || 0) + (rate ? rate.cents : 0);
      const text = formatMoney(cents, valueEl.dataset.moneyFormat || this.dataset.moneyFormat);
      valueEl.textContent = text;
      valueEl.setAttribute('value', text);
    }

    const key = rate ? 'withShipping' : 'withoutShipping';
    for (const selector of ['[data-fb-total-label]', '[data-fb-total-note]']) {
      const target = summary.querySelector(selector);
      if (target instanceof HTMLElement && target.dataset[key]) target.textContent = target.dataset[key];
    }
  }

  /* ------------------------------ cálculo -------------------------------- */

  async #calculate() {
    if (this.#busy) return;

    const digits = onlyDigits(this.input.value);
    // Prefixo fora das faixas dos Correios = CEP que não existe.
    if (digits.length !== 8 || !provinceFromZip(digits)) {
      this.#setMessage(this.dataset.errorInvalidZip, true);
      this.#renderRates([]);
      this.#setShippingValue(null);
      return;
    }

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#setBusy(true);
    this.#setMessage(this.dataset.loading, false);

    const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', maskZip(digits));
    params.set('shipping_address[country]', this.dataset.country || 'BR');

    // A API da loja exige o estado; sem ele o POST volta 422 ("Estado é obrigatório")
    // e nenhuma transportadora é consultada.
    params.set('shipping_address[province]', provinceFromZip(digits));

    try {
      await this.#withQuotedVariant(cartUrl, () => this.#quote(cartUrl, params, signal));
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#setMessage(this.dataset.errorGeneric, true);
      this.#renderRates([]);
      this.#setShippingValue(null);
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Na PDP, põe a variante selecionada no carrinho enquanto `run` cota e devolve a
   * linha à quantidade anterior no fim (mesmo em erro/abort). Fora da PDP só roda `run`.
   *
   * ponytail: cotação via carrinho temporário (add -> cota -> change). A API pública
   * não cota variante avulsa. Limites: com carrinho cheio a cotação inclui os outros
   * itens (é o frete real do pedido); entre o add e o change o carrinho fica alterado
   * por ~1–3s (outra aba veria). Se incomodar, trocar por app/Carrier Service com
   * endpoint próprio de cotação por SKU.
   * @param {string} cartUrl
   * @param {() => Promise<void>} run
   */
  async #withQuotedVariant(cartUrl, run) {
    if (this.dataset.quoteVariant == null) return run();

    const scope = this.closest('.shopify-section') || document;
    const id = Number(scope.querySelector('[data-fb-variant-id]')?.value);
    const quantity = Math.max(1, Number(scope.querySelector('[data-fb-qty-input]')?.value) || 1);
    if (!id) return run();

    const added = await fetch(`${cartUrl}/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }] }),
    });
    if (!added.ok) {
      // 422 aqui = variante sem estoque / indisponível; não há o que cotar.
      this.#setMessage(this.dataset.errorUnavailable || this.dataset.errorGeneric, true);
      this.#renderRates([]);
      return;
    }
    const line = (await added.json())?.items?.[0];

    try {
      await run();
    } finally {
      // Sem `signal`: a restauração não pode ser abortada.
      if (line?.key) {
        await fetch(`${cartUrl}/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - quantity) }),
        }).catch(() => {});
      }
    }
  }

  /**
   * @param {string} cartUrl
   * @param {URLSearchParams} params
   * @param {AbortSignal} signal
   */
  async #quote(cartUrl, params, signal) {
    const prepared = await fetch(`${cartUrl}/prepare_shipping_rates.json?${params.toString()}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!prepared.ok) {
      // O 422 do Shopify traz os erros por campo (ex.: {"province":["Selecione um
      // estado"]} quando a loja exige estado no endereço). Mostrar a mensagem real
      // do servidor em vez de culpar o CEP — só cai no texto genérico se não vier
      // nenhuma mensagem utilizável.
      let serverMessage = '';
      try {
        const payload = await prepared.clone().json();
        serverMessage = Object.values(payload || {})
          .flat()
          .filter((entry) => typeof entry === 'string')
          .join(' ');
      } catch {
        serverMessage = '';
      }

      const fallback = prepared.status === 422 ? this.dataset.errorZip : this.dataset.errorGeneric;
      this.#setMessage(serverMessage || fallback, true);
      this.#renderRates([]);
      this.#setShippingValue(null);
      return;
    }

    const rates = await this.#pollRates(cartUrl, signal);

    if (!rates.length) {
      this.#setMessage(this.dataset.errorEmpty, true);
      this.#renderRates([]);
      this.#setShippingValue(null);
      return;
    }

    // Recotação: mantém a transportadora já escolhida se ela continua disponível.
    const { rate: chosen } = this.#readState();
    const index = Math.max(0, rates.findIndex((rate) => rate.name === chosen));

    this.#setMessage('', false);
    this.#ratedZip = onlyDigits(params.get('shipping_address[zip]') || '');
    this.#renderRates(rates, index);
    this.#select(index, rates);
  }

  /**
   * @param {string} cartUrl
   * @param {AbortSignal} signal
   * @returns {Promise<Array<{name: string, cents: number}>>}
   */
  async #pollRates(cartUrl, signal) {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
      const response = await fetch(`${cartUrl}/async_shipping_rates.json`, {
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) throw new Error(`async_shipping_rates: ${response.status}`);

      const text = (await response.text()).trim();

      if (text && text !== 'null') {
        const data = JSON.parse(text);
        const list = Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
        return list.map((rate) => ({
          name: String(rate?.name ?? rate?.presentment_name ?? ''),
          cents: Math.round(parseFloat(String(rate?.price ?? '0').replace(',', '.')) * 100) || 0,
        }));
      }

      await wait(POLL_INTERVAL);
    }

    throw new Error('async_shipping_rates: timeout');
  }

  /**
   * @param {Array<{name: string, cents: number}>} rates
   * @param {number} [selected]
   */
  #renderRates(rates, selected = 0) {
    if (!this.ratesEl) return;

    this.ratesEl.textContent = '';
    this.ratesEl.hidden = rates.length === 0;
    if (!rates.length) return;

    const name = `fb-ship-rate-${Math.random().toString(36).slice(2, 8)}`;

    rates.forEach((rate, index) => {
      const item = document.createElement('li');
      const label = document.createElement('label');
      label.className = 'fb-ship__rate';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = name;
      radio.value = String(index);
      radio.checked = index === selected;
      radio.addEventListener('change', () => this.#select(index, rates));

      const rateName = document.createElement('span');
      rateName.className = 'fb-ship__rate-name';
      rateName.textContent = rate.name;

      const ratePrice = document.createElement('span');
      ratePrice.className = 'fb-ship__rate-price';
      ratePrice.textContent = this.#money(rate.cents);

      label.append(radio, rateName, ratePrice);
      item.append(label);
      this.ratesEl.append(item);
    });
  }

  /**
   * @param {number} index
   * @param {Array<{name: string, cents: number}>} rates
   */
  #select(index, rates) {
    const rate = rates[index];
    if (!rate) return;
    this.#writeState({ rate: rate.name });
    this.#setShippingValue(rate);
  }

  /** @param {number} cents */
  #money(cents) {
    if (cents === 0) return this.dataset.freeLabel || 'Grátis';
    return formatMoney(cents, this.dataset.moneyFormat);
  }
}

if (!customElements.get('flabom-shipping-calculator')) {
  customElements.define('flabom-shipping-calculator', FlabomShippingCalculator);
}
